import { zodResolver } from "@hookform/resolvers/zod";
import { Loading } from "@mnk/frontend/components/Loading";
import { PrimaryButton } from "@mnk/frontend/components/ui/button/PrimaryButton";
import { Success } from "@mnk/frontend/components/ui/flashMessage/Success";
import { trpc } from "@mnk/frontend/lib/trpc";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export const USER_ROLES_WITH_LABELS = [
  { label: "管理者", value: "admin" },
  { label: "一般", value: "general" },
] as const;

const newUserInputSchema = z.object({
  email: z.string().email("メールアドレスが不正です"),
  officeId: z.string().min(1, "事業所を選択してください"),
  password: z.string().min(1, "パスワードを入力してください"),
  passwordConfirmation: z.string().min(1, "パスワードを入力してください"),
  name: z.string().min(1, "名前を入力してください"),
  role: z.string().refine((role) => USER_ROLES_WITH_LABELS.some((r) => r.value === role), {
    message: "不正なロールです",
  }),
});

export const NewForm = () => {
  const [processing, setProcessing] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const getOffices = trpc.getOffices.useQuery();
  const createUserMutation = trpc.createUser.useMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<z.infer<typeof newUserInputSchema>>({
    resolver: zodResolver(newUserInputSchema),
    defaultValues: {
      email: "",
      officeId: "",
      name: "",
      role: "general",
      password: "",
      passwordConfirmation: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof newUserInputSchema>) => {
    try {
      setProcessing(true);
      await createUserMutation.mutateAsync(data);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  if (getOffices.isLoading) return <Loading />;

  return (
    <div className="w-full bg-white rounded-lg">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <p className="text-xl font-bold">アカウント設定</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8 px-4 py-8">
        <div className="flex flex-col gap-2">
          <p className="text-sm font-bold">名前</p>
          <input type="text" className="w-full p-2 border border-gray-200 rounded-md" {...register("name")} />
          {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-bold">メールアドレス</p>
          <input type="text" className="w-full p-2 border border-gray-200 rounded-md" {...register("email")} />
          {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-bold">事業所名</p>
          <select className="w-full p-2 border border-gray-200 rounded-md" {...register("officeId")}>
            {getOffices.data?.map((office) => (
              <option key={office.id} value={office.id}>
                {office.name}
              </option>
            ))}
          </select>
          {errors.officeId && <p className="text-sm text-red-500">{errors.officeId.message}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-bold">ロール</p>
          <select className="w-full p-2 border border-gray-200 rounded-md" {...register("role")}>
            {USER_ROLES_WITH_LABELS.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
          {errors.role && <p className="text-sm text-red-500">{errors.role.message}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-bold">パスワード</p>
          <input type="password" className="w-full p-2 border border-gray-200 rounded-md" {...register("password")} />
          {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-bold">パスワード確認</p>
          <input
            type="password"
            className="w-full p-2 border border-gray-200 rounded-md"
            {...register("passwordConfirmation")}
          />
          {errors.passwordConfirmation && <p className="text-sm text-red-500">{errors.passwordConfirmation.message}</p>}
        </div>

        <PrimaryButton className="w-full" type="submit" disabled={!isDirty || processing}>
          作成
        </PrimaryButton>
      </form>
      {showSuccessMessage && <Success message="ユーザーを作成しました" onClose={() => setShowSuccessMessage(false)} />}
    </div>
  );
};
